import { useEffect, useState } from "react";
import { ConditionPreset, APICategory } from "../../../../../models/Entities";
import { immutibleArray } from "../../../../../utils/immutible";
import { useParams } from "react-router-dom";
import { isDefaultFilter } from "./helpers";
import { RELATIVE_DATE_CONDITION_OPERATORS } from "../constants";

type UseValidateConditionPresetsProps = {
  remoteConditionPresets: ConditionPreset[];
  conditionPresets: Partial<ConditionPreset>[];
};

const useValidateConditionPresets = ({
  remoteConditionPresets,
  conditionPresets,
}: UseValidateConditionPresetsProps) => {
  // state
  const [conditionPresetErrors, setConditionPresetErrors] = useState<(string | undefined)[]>([]);

  // hooks
  const { category } = useParams<{ category: APICategory }>();

  // event handlers
  const validateChanges = () => {
    let hasErrors = false;

    const newConditionPresetErrors = conditionPresets.map((conditionPreset) => {
      if (!conditionPreset.common_model) {
        hasErrors = true;
        return "Please select a model.";
      }
      if (!conditionPreset.normalized_key_name) {
        hasErrors = true;
        return "Please select a field.";
      }
      if (!conditionPreset.is_end_user_configured && !conditionPreset.operator) {
        hasErrors = true;
        return "Please select an operator.";
      }
      if (
        !conditionPreset.is_end_user_configured &&
        (conditionPreset.value === undefined ||
          conditionPreset.value === null ||
          conditionPreset.value === "")
      ) {
        // default filters can have empty values
        if (isDefaultFilter(conditionPreset, remoteConditionPresets)) {
          return undefined;
        }
        hasErrors = true;
        return "Please select a value.";
      }
      if (
        !conditionPreset.is_end_user_configured &&
        conditionPreset.operator &&
        RELATIVE_DATE_CONDITION_OPERATORS.includes(conditionPreset.operator) &&
        !conditionPreset.period
      ) {
        hasErrors = true;
        return "Please select a period.";
      }
      return undefined;
    });

    setConditionPresetErrors(newConditionPresetErrors);
    return hasErrors;
  };

  const onConditionPresetErrorChange = (index: number) => {
    setConditionPresetErrors((conditionPresetErrors) =>
      immutibleArray.replace(conditionPresetErrors, index, undefined),
    );
  };

  const onConditionPresetErrorDelete = (index: number) => {
    setConditionPresetErrors((conditionPresetErrors) =>
      immutibleArray.remove(conditionPresetErrors, index),
    );
  };

  // effects
  // reset error state on category change
  useEffect(() => {
    setConditionPresetErrors([]);
  }, [category]);

  return {
    validateChanges,
    conditionPresetErrors,
    onConditionPresetErrorChange,
    onConditionPresetErrorDelete,
  };
};

export default useValidateConditionPresets;
